/*
 * Copyright (c) 2021 - 2025, Ludvig Lundgren and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Field as FormikField, type FieldProps } from "formik";
import { MultiSelect as RMSC } from "react-multi-select-component";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { APIClient } from "@api/APIClient";
import { ReleaseKeys } from "@api/query_keys";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { DurationFieldWide, SwitchGroupWide, TextFieldWide } from "@components/inputs";
import { SlideOver } from "@components/panels";
import { AddFormProps, UpdateFormProps } from "@forms/_shared";
import { classNames } from "@utils";
import { getPushStatusOptions } from "@domain/constants";

export function CleanupJobAddForm({isOpen, toggle}: AddFormProps) {
  const { t } = useTranslation(["options", "settings"]);
  const pushStatusOptions = getPushStatusOptions(t);
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: (job: ReleaseCleanupJob) => APIClient.release.cleanupJobs.store(job),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ReleaseKeys.cleanupJobs.lists()});
      toast.custom((toastInstance) => <Toast type="success" body={t("settings:forms.cleanupJob.created")} t={toastInstance}/>);
      toggle();
    },
    onError: () => {
      toast.custom((toastInstance) => <Toast type="error" body={t("settings:forms.cleanupJob.createFailed")} t={toastInstance}/>);
    }
  });

  const onSubmit = (data: unknown) => addMutation.mutate(data as ReleaseCleanupJob);

  const initialValues: Partial<ReleaseCleanupJob> = {
    name: "",
    enabled: true,
    schedule: "0 3 * * *",    // Default: Daily at 3 AM
    older_than: 720,           // Default: 30 days in hours
    indexers: "",
    statuses: ""
  };

  const {data: indexerOptions} = useQuery<IndexerDefinition[], Error, { identifier: string; name: string; }[]>({
    queryKey: ['indexers'],
    queryFn: () => APIClient.indexers.getAll(),
    select: data => data.map(indexer => ({
      identifier: indexer.identifier,
      name: indexer.name
    })),
  });

  return (
    <SlideOver
      type="CREATE"
      title={t("settings:forms.cleanupJob.title")}
      isOpen={isOpen}
      toggle={toggle}
      onSubmit={onSubmit}
      initialValues={initialValues}
    >
      {() => (
        <div className="py-2 space-y-6 sm:py-0 sm:space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
          <TextFieldWide required name="name" label={t("settings:releases.name")} placeholder={t("settings:forms.cleanupJob.namePlaceholder")}/>

          <SwitchGroupWide name="enabled" label={t("settings:releases.enabled")} description={t("settings:forms.cleanupJob.enableDesc")}/>

          <TextFieldWide
            required
            name="schedule"
            label={t("settings:forms.cleanupJob.schedule")}
            placeholder={t("settings:forms.cleanupJob.schedulePlaceholder")}
            help={t("settings:forms.cleanupJob.scheduleHelp")}
          />

          <DurationFieldWide
            required
            name="older_than"
            label={t("settings:forms.cleanupJob.olderThan")}
            placeholder={t("settings:forms.cleanupJob.olderThanPlaceholder")}
            help={t("settings:forms.cleanupJob.olderThanHelp")}
            defaultValue={30}
            defaultUnit="days"
            units={["hours", "days", "weeks", "months", "years"]}
            storeAsHours={true}
          />

          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 px-4 sm:px-6">
            <label className="text-sm font-medium text-gray-900 dark:text-white">
              {t("settings:forms.cleanupJob.indexersOptional")}
            </label>
            <div className="col-span-2">
              <FormikField name="indexers">
                {({field, form}: FieldProps) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                  >
                    <RMSC
                      options={indexerOptions?.map(opt => ({value: opt.identifier, label: opt.name})) || []}
                      value={
                        !field.value || field.value === '' || !indexerOptions
                          ? []
                          : field.value
                            .split(',')
                            .filter(Boolean)
                            .map((v: string) => {
                              const option = indexerOptions.find(opt => opt.identifier === v);
                              return option ? {value: option.identifier, label: option.name} : null;
                            })
                            .filter((item: { value: string; label: string } | null): item is { value: string; label: string } => item !== null)
                      }
                      onChange={(selected: { value: string; label: string }[]) => {
                        const indexerString = selected.map(s => s.value).join(',');
                        form.setFieldValue("indexers", indexerString);
                      }}
                      labelledBy="cleanup-job-add-indexers"
                    />
                  </div>
                )}
              </FormikField>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("settings:forms.cleanupJob.leaveEmptyIndexers")}
              </p>
            </div>
          </div>

          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 px-4 sm:px-6">
            <label className="text-sm font-medium text-gray-900 dark:text-white">
              {t("settings:forms.cleanupJob.statusesOptional")}
            </label>
            <div className="col-span-2">
              <FormikField name="statuses">
                {({field, form}: FieldProps) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                  >
                    <RMSC
                      options={pushStatusOptions}
                      value={
                        !field.value || field.value === ''
                          ? []
                          : field.value
                            .split(',')
                            .filter(Boolean)
                            .map((v: string) => {
                              const option = pushStatusOptions.find(opt => opt.value === v);
                              return option ? {value: option.value, label: option.label} : null;
                            })
                            .filter((item: { value: string; label: string } | null): item is { value: string; label: string } => item !== null)
                      }
                      onChange={(selected: { value: string; label: string }[]) => {
                        const statusString = selected.map(s => s.value).join(',');
                        form.setFieldValue("statuses", statusString);
                      }}
                      labelledBy="cleanup-job-add-statuses"
                    />
                  </div>
                )}
              </FormikField>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("settings:forms.cleanupJob.leaveEmptyStatuses")}
              </p>
            </div>
          </div>
        </div>
      )}
    </SlideOver>
  );
}

export function CleanupJobUpdateForm({isOpen, toggle, data: job}: UpdateFormProps<ReleaseCleanupJob>) {
  const { t } = useTranslation(["options", "settings"]);
  const pushStatusOptions = getPushStatusOptions(t);
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (job: ReleaseCleanupJob) => APIClient.release.cleanupJobs.update(job),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ReleaseKeys.cleanupJobs.lists()});
      toast.custom((toastInstance) => <Toast type="success" body={t("settings:forms.cleanupJob.updated")} t={toastInstance}/>);
      toggle();
    },
    onError: () => {
      toast.custom((toastInstance) => <Toast type="error" body={t("settings:forms.cleanupJob.updateFailed")} t={toastInstance}/>);
    }
  });

  const onSubmit = (data: unknown) => updateMutation.mutate(data as ReleaseCleanupJob);

  const deleteMutation = useMutation({
    mutationFn: (jobId: number) => APIClient.release.cleanupJobs.delete(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ReleaseKeys.cleanupJobs.lists()});
      queryClient.invalidateQueries({queryKey: ReleaseKeys.cleanupJobs.detail(job.id)});
      toast.custom((toastInstance) => <Toast type="success" body={t("settings:forms.cleanupJob.deleted", { name: job.name })} t={toastInstance}/>);
      toggle();
    },
  });

  const onDelete = () => deleteMutation.mutate(job.id);

  const initialValues: ReleaseCleanupJob = {
    id: job.id,
    name: job.name,
    enabled: job.enabled,
    schedule: job.schedule,
    older_than: job.older_than,
    indexers: job.indexers,
    statuses: job.statuses,
    // Read-only fields
    last_run: job.last_run,
    last_run_status: job.last_run_status,
    last_run_data: job.last_run_data,
    next_run: job.next_run,
    created_at: job.created_at,
    updated_at: job.updated_at
  };

  // Get indexer options for multi-select
  const {data: indexerOptions} = useQuery<IndexerDefinition[], Error, { identifier: string; name: string; }[]>({
    queryKey: ['indexers'],
    queryFn: () => APIClient.indexers.getAll(),
    select: data => data.map(indexer => ({
      identifier: indexer.identifier,
      name: indexer.name
    })),
  });

  return (
    <SlideOver
      type="UPDATE"
      title={t("settings:forms.cleanupJob.title")}
      isOpen={isOpen}
      toggle={toggle}
      deleteAction={onDelete}
      onSubmit={onSubmit}
      initialValues={initialValues}
    >
      {() => (
        <div className="py-2 space-y-6 sm:py-0 sm:space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
          {/* Same fields as AddForm */}
          <TextFieldWide required name="name" label={t("settings:releases.name")} placeholder={t("settings:forms.cleanupJob.namePlaceholder")}/>

          <SwitchGroupWide name="enabled" label={t("settings:releases.enabled")} description={t("settings:forms.cleanupJob.enableDesc")}/>

          <TextFieldWide
            required
            name="schedule"
            label={t("settings:forms.cleanupJob.schedule")}
            placeholder={t("settings:forms.cleanupJob.schedulePlaceholder")}
            help={t("settings:forms.cleanupJob.scheduleHelp")}
          />

          <DurationFieldWide
            required
            name="older_than"
            label={t("settings:forms.cleanupJob.olderThan")}
            placeholder={t("settings:forms.cleanupJob.olderThanPlaceholder")}
            help={t("settings:forms.cleanupJob.olderThanHelp")}
            defaultValue={30}
            defaultUnit="days"
            units={["hours", "days", "weeks", "months", "years"]}
            storeAsHours={true}
          />

          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 px-4 sm:px-6">
            <label className="text-sm font-medium text-gray-900 dark:text-white">
              {t("settings:forms.cleanupJob.indexersOptional")}
            </label>
            <div className="col-span-2">
              <FormikField name="indexers">
                {({field, form}: FieldProps) => {
                  const computedValue = !field.value || field.value === '' || !indexerOptions
                    ? []
                    : field.value
                      .split(',')
                      .filter(Boolean)
                      .map((v: string) => {
                        const option = indexerOptions.find(opt => opt.identifier === v);
                        return option ? {value: option.identifier, label: option.name} : null;
                      })
                      .filter((item: { value: string; label: string } | null): item is { value: string; label: string } => item !== null);

                  return (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                    >
                      <RMSC
                        options={indexerOptions?.map(opt => ({value: opt.identifier, label: opt.name})) || []}
                        value={computedValue}
                        onChange={(selected: { value: string; label: string }[]) => {
                          const indexerString = selected.map(s => s.value).join(',');
                          form.setFieldValue("indexers", indexerString);
                        }}
                        labelledBy="cleanup-job-edit-indexers"
                      />
                    </div>
                  );
                }}
              </FormikField>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("settings:forms.cleanupJob.leaveEmptyIndexers")}
              </p>
            </div>
          </div>

          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 px-4 sm:px-6">
            <label className="text-sm font-medium text-gray-900 dark:text-white">
              {t("settings:forms.cleanupJob.statusesOptional")}
            </label>
            <div className="col-span-2">
              <FormikField name="statuses">
                {({field, form}: FieldProps) => {
                  const computedValue = !field.value || field.value === ''
                    ? []
                    : field.value
                      .split(',')
                      .filter(Boolean)
                      .map((v: string) => {
                        const option = pushStatusOptions.find(opt => opt.value === v);
                        return option ? {value: option.value, label: option.label} : null;
                      })
                      .filter((item: { value: string; label: string } | null): item is { value: string; label: string } => item !== null);

                  return (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                    >
                      <RMSC
                        options={pushStatusOptions}
                        value={computedValue}
                        onChange={(selected: { value: string; label: string }[]) => {
                          const statusString = selected.map(s => s.value).join(',');
                          form.setFieldValue("statuses", statusString);
                        }}
                        labelledBy="cleanup-job-edit-statuses"
                      />
                    </div>
                  );
                }}
              </FormikField>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t("settings:forms.cleanupJob.leaveEmptyStatuses")}
              </p>
            </div>
          </div>

          {/* Execution History Section */}
          {job.last_run !== "0001-01-01T00:00:00Z" && (
            <div className="py-4 sm:py-5 px-4 sm:px-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                {t("settings:forms.cleanupJob.executionHistory")}
              </h3>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">{t("settings:forms.cleanupJob.lastRun")}</dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {format(new Date(job.last_run), "MMM d, yyyy HH:mm:ss")}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">{t("settings:forms.cleanupJob.status")}</dt>
                  <dd>
                    <span className={classNames(
                      "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                      job.last_run_status === "SUCCESS"
                        ? "bg-green-100 text-green-700 dark:bg-green-400/10 dark:text-green-400"
                        : "bg-red-100 text-red-700 dark:bg-red-400/10 dark:text-red-400"
                    )}>
                      {job.last_run_status}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      )}
    </SlideOver>
  );
}
